import {
  ChakraBox,
  ChakraFlex,
  CircularProgress,
  FAIconName,
  FireflyButton,
  ModalV2,
  Text,
} from '@equip.health/ui';
import { Formik, FormikProps } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EditTreatmentOverviewFormData } from '~/lib/types/notes/EditTreatmentOverviewFormData';
import {
  EditTreatmentOverviewForm,
  TreatmentOverviewFormStep,
} from './EditTreatmentOverviewForm';
import { useTreatmentAttributes } from '~/lib/hooks/useTreatmentAttributes';
import Loading from '~/pages/loading';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { useInitialTreatmentOverviewFormValues } from '~/lib/hooks/useInitialTreatmentOverviewFormValues';
import { createTreatmentEpisodeAttributesPayload } from '~/lib/util/treatmentEpisodes.util';
import { useCreateAttributesMutation } from '~/store/services/treatmentAttributes';
import { CreateAttributesParams } from '~/lib/types/treatmentAttributes/treatmentAttributes';
import { useGetTreatmentModalitiesEnumQuery } from '~/store/services/enumerations';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';

const ID = 'edit-treatment-overview-modal';

const EDIT_TREATMENT_OVERVIEW_MODAL_TITLE_MAP = {
  [TreatmentOverviewFormStep.AttributesStep]: 'Edit overview',
  [TreatmentOverviewFormStep.ExplanationsStep]:
    'Please provide a reason for each change',
};

type Props = {
  patientId: string;
  noteId?: string;
  isOpen: boolean;
  onClose: () => void;
};
export const EditTreatmentOverviewModal = ({
  patientId,
  noteId,
  isOpen,
  onClose,
}: Props) => {
  const { trackEvent } = useAnalytics();

  const {
    data: treatmentModalities,
    isLoading: isGetTreatmentModalitiesLoading,
  } = useGetTreatmentModalitiesEnumQuery();

  const {
    activeTreatmentEpisode,
    discussionOfWeights,
    modalities,
    forms,
    phaseStages,
    levels,
    refeedingRisks,
    saferStages,
    medicalStabilities,
    psychiatricStabilities,
    isLoading: isGetPatientAttributesLoading,
  } = useTreatmentAttributes(patientId);

  const [createAttributes, { isLoading: isCreatingAttributes }] =
    useCreateAttributesMutation();

  const { showErrorToast } = useErrorToast();

  const formikRef = useRef<FormikProps<EditTreatmentOverviewFormData>>(null);

  // isCreatingAttributes lags a repeated Enter keydown, which can fire again before React
  // commits the disabled Save button - so the handler needs its own synchronous guard.
  const isSubmitInFlightRef = useRef(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const savingBodyMinHeightRef = useRef(0);

  const [treatmentOverviewFormStep, setTreatmentOverviewFormStep] =
    useState<TreatmentOverviewFormStep>(
      TreatmentOverviewFormStep.AttributesStep,
    );

  const handleBack = () => {
    setTreatmentOverviewFormStep(TreatmentOverviewFormStep.AttributesStep);
  };

  const handleNext = () => {
    setTreatmentOverviewFormStep(TreatmentOverviewFormStep.ExplanationsStep);
  };

  const handleClose = () => {
    setTreatmentOverviewFormStep(TreatmentOverviewFormStep.AttributesStep);
    onClose();
  };

  const handleSubmit = async (values: EditTreatmentOverviewFormData) => {
    if (isSubmitInFlightRef.current) {
      return;
    }
    isSubmitInFlightRef.current = true;
    // Captured while the form is still mounted, so swapping it for the saving state below does
    // not shrink the modal and move the footer.
    savingBodyMinHeightRef.current = bodyRef.current?.offsetHeight ?? 0;

    try {
      const attributes: CreateAttributesParams['attributes'] = {
        ...createTreatmentEpisodeAttributesPayload(values),
        ...(noteId ? { noteId } : {}),
      };

      await createAttributes({
        attributes,
        treatmentEpisodeId: activeTreatmentEpisode.treatmentEpisodeId,
      }).unwrap();
      handleClose();
    } catch (error) {
      showErrorToast(
        GlobalErrorCode.CREATE_ATTRIBUTES,
        error.status,
        error.data?.message,
      );
    } finally {
      isSubmitInFlightRef.current = false;
    }
  };

  const initialEditTreatmentOverviewFormValues =
    useInitialTreatmentOverviewFormValues({
      discussionOfWeights,
      forms,
      isLoading: isGetPatientAttributesLoading,
      levels,
      medicalStabilities,
      modalities,
      phaseStages,
      psychiatricStabilities,
      refeedingRisks,
      saferStages,
    });

  const hasExplanationsForChanges = useCallback(
    (formValues: EditTreatmentOverviewFormData) => {
      return (
        Object.keys(initialEditTreatmentOverviewFormValues) as Array<
          keyof EditTreatmentOverviewFormData
        >
      ).every((attribute) => {
        if (
          attribute === 'form' &&
          (formValues[attribute] === null ||
            formValues[attribute] === undefined)
        ) {
          return true;
        }

        if (
          (initialEditTreatmentOverviewFormValues[attribute] ?? '') ===
          (formValues[attribute] ?? '')
        ) {
          return true;
        }

        return !!formValues[attribute]?.explanation;
      });
    },
    [initialEditTreatmentOverviewFormValues],
  );

  const isLoading =
    isGetTreatmentModalitiesLoading || isGetPatientAttributesLoading;

  useEffect(() => {
    if (formikRef.current && isOpen) {
      formikRef.current.setValues(initialEditTreatmentOverviewFormValues);
    }
  }, [initialEditTreatmentOverviewFormValues, isOpen]);

  const hasAnyAttributeChanged = (
    formValues: EditTreatmentOverviewFormData,
  ) => {
    return Object.keys(formValues).some(
      (key) =>
        formValues[key]?.code !==
          initialEditTreatmentOverviewFormValues[key]?.code ||
        formValues[key]?.id !== initialEditTreatmentOverviewFormValues[key]?.id,
    );
  };

  const hasModalityFormOrPhaseStageChanged = (
    formValues: EditTreatmentOverviewFormData,
  ) => {
    return (
      initialEditTreatmentOverviewFormValues?.modality !==
        formValues?.modality ||
      initialEditTreatmentOverviewFormValues?.form !== formValues?.form ||
      initialEditTreatmentOverviewFormValues?.phaseStage !==
        formValues?.phaseStage
    );
  };

  const hasModalityFormPhaseStageFullyFilled = (
    formValues: EditTreatmentOverviewFormData,
  ) => {
    if (
      !initialEditTreatmentOverviewFormValues.modality &&
      !formValues.modality
    ) {
      return true;
    }

    if (!formValues.phaseStage?.code) {
      return false;
    }
    const modalityEnum = treatmentModalities?.find(
      (m) => m.code === formValues.modality?.code,
    );
    if (!modalityEnum) {
      return false;
    }
    if (modalityEnum.treatmentForms.length > 0) {
      return !!formValues.form?.code;
    }
    return true;
  };

  return (
    <Formik
      initialValues={initialEditTreatmentOverviewFormValues}
      innerRef={formikRef}
      onSubmit={handleSubmit}
    >
      {({ submitForm, values }) => (
        <ModalV2
          bodyOverflow={
            treatmentOverviewFormStep ===
            TreatmentOverviewFormStep.AttributesStep
              ? 'unset'
              : 'auto'
          }
          // it may be simpler/more self-contained to put the footers in the Steps
          footerContent={
            <ChakraFlex justifyContent="space-between" width="100%">
              {treatmentOverviewFormStep ===
                TreatmentOverviewFormStep.AttributesStep && (
                <>
                  <FireflyButton
                    id={`${ID}-cancel`}
                    isDisabled={isCreatingAttributes}
                    leftIcon={FAIconName.XmarkRegular}
                    onClick={() => {
                      trackEvent(
                        CarePlanEvents.clickOnCancelButtonInEditOverviewModal,
                      );
                      handleClose();
                    }}
                    variant="secondary"
                  >
                    Cancel
                  </FireflyButton>
                  <FireflyButton
                    id={`${ID}-next`}
                    isDisabled={
                      !hasAnyAttributeChanged(values) ||
                      (hasModalityFormOrPhaseStageChanged(values) &&
                        !hasModalityFormPhaseStageFullyFilled(values))
                    }
                    onClick={() => {
                      trackEvent(
                        CarePlanEvents.clickOnNextButtonInEditOverviewModal,
                      );
                      handleNext();
                    }}
                    rightIcon={FAIconName.arrowRightRegular}
                    variant="primary"
                  >
                    Next
                  </FireflyButton>
                </>
              )}
              {treatmentOverviewFormStep ===
                TreatmentOverviewFormStep.ExplanationsStep && (
                <>
                  <FireflyButton
                    id={`${ID}-back`}
                    isDisabled={isCreatingAttributes}
                    leftIcon={FAIconName.arrowLeftRegular}
                    onClick={() => {
                      trackEvent(
                        CarePlanEvents.clickOnBackButtonInEditOverviewModal,
                      );
                      handleBack();
                    }}
                    variant="secondary"
                  >
                    Back
                  </FireflyButton>
                  <FireflyButton
                    id={`${ID}-save`}
                    isDisabled={
                      !hasExplanationsForChanges(values) || isCreatingAttributes
                    }
                    isLoading={isCreatingAttributes}
                    leftIcon={FAIconName.checkRegular}
                    onClick={submitForm}
                    variant="primary"
                  >
                    Save and close
                  </FireflyButton>
                </>
              )}
            </ChakraFlex>
          }
          hideCloseButton={isCreatingAttributes}
          isOpen={isOpen}
          onClose={() => {
            trackEvent(CarePlanEvents.clickOnXIconInEditOverviewModal);
            handleClose();
          }}
          shouldCloseOnEscape={!isCreatingAttributes}
          shouldCloseOnOverlayClick={!isCreatingAttributes}
          size="5xl"
          title={
            EDIT_TREATMENT_OVERVIEW_MODAL_TITLE_MAP[treatmentOverviewFormStep]
          }
        >
          <ChakraBox
            ref={bodyRef}
            minHeight={
              isCreatingAttributes
                ? `${savingBodyMinHeightRef.current}px`
                : undefined
            }
          >
            {isCreatingAttributes && (
              <ChakraFlex
                alignItems="center"
                flexDirection="column"
                gap="16px"
                height="100%"
                justifyContent="center"
              >
                <CircularProgress
                  id={`${ID}-saving-progress`}
                  isIndeterminate
                />
                <Text color="neutral.secondary">Saving your changes...</Text>
              </ChakraFlex>
            )}
            {!isCreatingAttributes && isLoading && <Loading />}
            {!isCreatingAttributes && !isLoading && (
              <EditTreatmentOverviewForm
                oldValues={initialEditTreatmentOverviewFormValues}
                step={treatmentOverviewFormStep}
              />
            )}
          </ChakraBox>
        </ModalV2>
      )}
    </Formik>
  );
};
